import { useMutation } from '@apollo/client';

import LearnerProfileInterestedDomainsQuery from 'bundles/onboarding-common/queries/LearnerProfileInterestedDomains.graphql';
import UpdateInterestedDomains from 'bundles/onboarding-common/queries/UpdateInterestedDomains.graphql';
import type { LearnerProfileInterestedDomainsQueryQuery } from 'bundles/onboarding-common/queries/__generated__/LearnerProfileInterestedDomains';
import type {
  UpdateInterestedDomainsMutation,
  UpdateInterestedDomainsMutationVariables,
} from 'bundles/onboarding-common/queries/__generated__/UpdateInterestedDomains';

const useUpdateInterestedDomains = () => {
  const [updateInterestedDomainsMutation] = useMutation<
    UpdateInterestedDomainsMutation,
    UpdateInterestedDomainsMutationVariables
  >(UpdateInterestedDomains, {
    context: { clientName: 'gatewayGql' },
    errorPolicy: 'all',
    update: (cache, { data }) => {
      if (data?.LearnerProfile_PatchDomains.__typename === 'LearnerProfile_PatchSuccess') {
        const existingData = cache.readQuery<LearnerProfileInterestedDomainsQueryQuery>({
          query: LearnerProfileInterestedDomainsQuery,
        });

        if (existingData?.LearnerProfile?.me && data.LearnerProfile_PatchDomains.profile) {
          const updatedInterestedDomains = data.LearnerProfile_PatchDomains.profile.interestedDomains;

          cache.writeQuery<LearnerProfileInterestedDomainsQueryQuery>({
            query: LearnerProfileInterestedDomainsQuery,
            data: {
              ...existingData,
              LearnerProfile: {
                ...existingData.LearnerProfile,
                me: {
                  ...existingData.LearnerProfile.me,
                  interestedDomains: updatedInterestedDomains,
                },
              },
            },
          });
        }
      }
    },
  });

  const updateInterestedDomains = async ({ domains }: { domains?: string[] | null }) => {
    const variables: UpdateInterestedDomainsMutationVariables = {
      interestedDomains:
        !domains || domains.length === 0
          ? { remove: {} }
          : {
              desiredDomains: domains.map((id) => (id === 'other' ? { otherDomain: 'OTHER' } : { id })),
            },
    };

    const { errors, data } = await updateInterestedDomainsMutation({
      variables,
    });

    if (errors) {
      return { success: false, errors: errors.map((error) => error.message) };
    }

    if (data?.LearnerProfile_PatchDomains.__typename === 'LearnerProfile_PatchFailure') {
      return { success: false, errors: data?.LearnerProfile_PatchDomains.errors.map((error) => error.message) };
    }

    return { success: true, errors: [] };
  };

  return {
    updateInterestedDomains,
  };
};

export default useUpdateInterestedDomains;
